/**
 * Phase 4f.C (Feb 2026) — Stateless Shop-Warenkorb (localStorage).
 *
 * Der Cart lebt vollständig im Browser — nur beim Checkout schickt der
 * Client die validierten Lines an den Backend (`POST /api/shop/checkout/session`).
 * Pro Händler-Slug ein eigener Cart, damit Käufe mehrerer Händler getrennt
 * bleiben.
 *
 * Datenmodell pro Slot in localStorage:
 *   {
 *     dealer_id:   string,                     // canonical dealer.id (für Checkout)
 *     dealer_slug: string,                     // URL-Segment (z.B. "marketplace-bikes")
 *     dealer_name: string,
 *     items: [{ article_id, name, sku, price_gross, photo_path, quantity }],
 *     updated_at: ISO,
 *   }
 *
 * Public API:
 *   useShopCart(slug)          → { cart, addItem, removeItem, setQty, clear }
 *   getCartItemCount(slug)     → number   (z.B. für Header-Badge ohne Hook)
 */
import { useCallback, useEffect, useState } from "react";

const KEY_PREFIX = "tw_shop_cart_";

function _read(slug) {
    try {
        const raw = localStorage.getItem(KEY_PREFIX + slug);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function _write(slug, cart) {
    try {
        if (!cart || !cart.items || !cart.items.length) {
            localStorage.removeItem(KEY_PREFIX + slug);
        } else {
            localStorage.setItem(KEY_PREFIX + slug, JSON.stringify(cart));
        }
        // Notify listeners (current tab + cross-tab via storage event)
        window.dispatchEvent(new CustomEvent("tw_shop_cart_update", { detail: { slug } }));
    } catch {
        /* localStorage full — ignore */
    }
}

export function getCartItemCount(slug) {
    const c = _read(slug);
    if (!c || !c.items) return 0;
    return c.items.reduce((sum, it) => sum + (it.quantity || 0), 0);
}

export function getCartTotal(slug) {
    const c = _read(slug);
    if (!c || !c.items) return 0;
    return c.items.reduce((sum, it) => sum + (it.price_gross || 0) * (it.quantity || 0), 0);
}

export function useShopCart(slug) {
    const [cart, setCart] = useState(() => _read(slug));

    // Re-read on storage events (cross-tab) AND on our custom event (same-tab)
    useEffect(() => {
        const reload = (e) => {
            // Custom event passes the slug to filter; storage event fires on any key
            if (e?.detail?.slug && e.detail.slug !== slug) return;
            setCart(_read(slug));
        };
        window.addEventListener("storage", reload);
        window.addEventListener("tw_shop_cart_update", reload);
        return () => {
            window.removeEventListener("storage", reload);
            window.removeEventListener("tw_shop_cart_update", reload);
        };
    }, [slug]);

    const addItem = useCallback((dealerInfo, article, qty = 1) => {
        const cur = _read(slug) || {
            dealer_id: dealerInfo.id || dealerInfo.dealer_id,
            dealer_slug: slug,
            dealer_name: dealerInfo.company_name || dealerInfo.name || slug,
            items: [],
        };
        // Ensure all carts of this slug share the same dealer_id; if dealerInfo
        // arrives later, fill it in.
        if (!cur.dealer_id) cur.dealer_id = dealerInfo.id || dealerInfo.dealer_id;
        if (!cur.dealer_name) cur.dealer_name = dealerInfo.company_name || dealerInfo.name || slug;
        const existing = cur.items.find((it) => it.article_id === article.id);
        if (existing) {
            existing.quantity = Math.min(99, (existing.quantity || 0) + qty);
        } else {
            cur.items.push({
                article_id: article.id,
                name: article.name,
                sku: article.sku || "",
                price_gross: Number(article.price_gross || 0),
                photo_path: (article.photo_paths && article.photo_paths[0]) || null,
                quantity: Math.min(99, qty),
            });
        }
        cur.updated_at = new Date().toISOString();
        _write(slug, cur);
        setCart({ ...cur });
    }, [slug]);

    const removeItem = useCallback((articleId) => {
        const cur = _read(slug);
        if (!cur) return;
        cur.items = cur.items.filter((it) => it.article_id !== articleId);
        cur.updated_at = new Date().toISOString();
        _write(slug, cur);
        setCart(cur.items.length ? { ...cur } : null);
    }, [slug]);

    const setQty = useCallback((articleId, qty) => {
        const cur = _read(slug);
        if (!cur) return;
        const it = cur.items.find((x) => x.article_id === articleId);
        if (!it) return;
        const q = Math.max(1, Math.min(99, Number(qty) || 1));
        it.quantity = q;
        cur.updated_at = new Date().toISOString();
        _write(slug, cur);
        setCart({ ...cur });
    }, [slug]);

    const clear = useCallback(() => {
        localStorage.removeItem(KEY_PREFIX + slug);
        window.dispatchEvent(new CustomEvent("tw_shop_cart_update", { detail: { slug } }));
        setCart(null);
    }, [slug]);

    const itemCount = cart?.items?.reduce((s, it) => s + (it.quantity || 0), 0) || 0;
    const total = cart?.items?.reduce((s, it) => s + (it.price_gross || 0) * (it.quantity || 0), 0) || 0;

    return { cart, addItem, removeItem, setQty, clear, itemCount, total };
}
